import React, { useCallback, useEffect, useState } from "react";
import api, { brl, fmtDate, rotuloPagamento } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Search, Undo2, FileText, FileX2, Share2 } from "lucide-react";
import { MobileCards } from "@/components/MobileCards";
import { toast } from "sonner";
import { compartilharTexto } from "@/lib/whatsapp";
import { ComunicarClienteDialog } from "@/components/ComunicarClienteDialog";
import { useRefreshListener, brlOrDash } from "@/lib/refresh";

export default function Payments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [est, setEst] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [estLoading, setEstLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(`/payments?q=${encodeURIComponent(q)}`);
    setItems(data); setLoading(false);
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useRefreshListener(load);
  const [recibo, setRecibo] = useState(null);

  const estornar = async () => {
    if (!motivo.trim()) return toast.error("Informe o motivo do estorno");
    setEstLoading(true);
    try {
      await api.post(`/payments/${est.id}/estornar`, { motivo: motivo.trim() });
      toast.success("Pagamento estornado — comprovante de estorno disponível"); setEst(null); setMotivo(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setEstLoading(false); }
  };

  const canEstornar = user?.role === "administrador" || user?.role === "financeiro";

  const viewReceipt = async (p) => {
    try {
      const resp = await api.get(`/payments/${p.id}/receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(resp.data), "_blank");
    } catch { toast.error("Erro ao gerar recibo"); }
  };
  // Recibo: janela única "Comunicar cliente" (contexto recibo); estorno mantém texto simples compartilhável
  const sendReceipt = (p) => {
    if (!p.estornado) { setRecibo(p); return; }
    const link = `${process.env.REACT_APP_BACKEND_URL}/api/public/receipts/${p.recibo_token}/pdf`;
    const emp = user?.empresa?.nome ? ` — ${user.empresa.nome}` : "";
    const msg = `Olá ${p.cliente_nome}! Informamos que o pagamento de ${brl(p.valor_pago)} (${rotuloPagamento(p)}) foi ESTORNADO${emp}.\nComprovante de estorno: ${link}`;
    compartilharTexto({ telefone: p.cliente_telefone, texto: msg, cliente_id: p.cliente_id, titulo: "Comprovante de estorno", evento: "recibo_enviado", categoria: "Pagamento", ref: p.id });
  };

  return (
    <div className="space-y-5 fade-up" data-testid="payments-page">
      <div><h1 className="text-2xl font-extrabold tracking-tight">Pagamentos</h1><p className="text-slate-500 text-sm">Histórico financeiro de recebimentos.</p></div>
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente..." className="pl-9" data-testid="payments-search" />
      </div>
      <div className="trt-card overflow-hidden hidden lg:block">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Data</th><th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Parcela</th><th className="text-left px-4 py-3 hidden md:table-cell">Forma</th>
              <th className="text-left px-4 py-3">Valor</th><th className="text-right px-4 py-3">Ação</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></td></tr>
              : items.length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-slate-400">Nenhum pagamento.</td></tr>
              : items.map((p) => (
                <tr key={p.id} className={`border-t ${p.estornado ? "opacity-50" : "hover:bg-slate-50"}`} data-testid={`payment-row-${p.id}`}>
                  <td className="px-4 py-3">{fmtDate(p.data)}</td>
                  <td className="px-4 py-3 font-medium">{p.cliente_nome}</td>
                  <td className="px-4 py-3 hidden md:table-cell">{rotuloPagamento(p).replace("Parcela #", "#")}</td>
                  <td className="px-4 py-3 hidden md:table-cell">{p.forma_pagamento}</td>
                  <td className="px-4 py-3 font-bold text-emerald-600">{brlOrDash(p.valor_pago)}{p.quitacao_id && <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">Quitação antecipada</Badge>}
                    {p.estornado && <Badge variant="outline" className="ml-2 bg-red-50 text-red-600 border-red-200">Estornado</Badge>}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => viewReceipt(p)} data-testid={`receipt-btn-${p.id}`}
                      title={p.estornado ? "Comprovante de estorno" : "Recibo"} className={p.estornado ? "text-red-600" : ""}>
                      {p.estornado ? <FileX2 className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                    </Button>
                    {p.recibo_token && <Button size="sm" variant="ghost" onClick={() => sendReceipt(p)} data-testid={`receipt-share-${p.id}`}
                      title={p.estornado ? "Compartilhar comprovante de estorno" : "Compartilhar recibo"} className="text-emerald-600"><Share2 className="w-4 h-4" /></Button>}
                    {canEstornar && !p.estornado && (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { setEst(p); setMotivo(""); }} data-testid={`estornar-btn-${p.id}`}><Undo2 className="w-4 h-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden">
        <MobileCards items={items} loading={loading} empty="Nenhum pagamento." testid="payments-cards" render={(p) => (
          <div className={`space-y-2 ${p.estornado ? "opacity-60" : ""}`} data-testid={`payment-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><div className="font-semibold">{p.cliente_nome}</div><div className="text-xs text-slate-500">{fmtDate(p.data)} · {rotuloPagamento(p)} · {p.forma_pagamento}</div></div>
              <div className="text-right"><div className="font-bold mono text-emerald-600 num-nowrap">{brlOrDash(p.valor_pago)}</div>{p.quitacao_id && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Quitação</Badge>}{p.estornado && <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">Estornado</Badge>}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className={`gap-1 ${p.estornado ? "text-red-600" : ""}`} onClick={() => viewReceipt(p)} data-testid={`receipt-card-btn-${p.id}`}>{p.estornado ? <FileX2 className="w-4 h-4" /> : <FileText className="w-4 h-4" />} {p.estornado ? "Comprovante" : "Recibo"}</Button>
              {p.recibo_token && <Button size="sm" variant="outline" className="gap-1 text-emerald-700 border-emerald-200" onClick={() => sendReceipt(p)} data-testid={`receipt-card-share-${p.id}`}><Share2 className="w-4 h-4" /> Compartilhar</Button>}
              {canEstornar && !p.estornado && <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200" onClick={() => { setEst(p); setMotivo(""); }} data-testid={`estornar-card-btn-${p.id}`}><Undo2 className="w-4 h-4" /> Estornar</Button>}
            </div>
          </div>
        )} />
      </div>

      {recibo && <ComunicarClienteDialog contexto="recibo" parcelaId={recibo.installment_id} paymentId={recibo.id} onClose={() => setRecibo(null)} />}
      <Dialog open={!!est} onOpenChange={(v) => !v && setEst(null)}>
        <DialogContent data-testid="estorno-modal">
          <DialogHeader><DialogTitle>Estornar pagamento?</DialogTitle>
            <DialogDescription>O valor de {brl(est?.valor_pago)} será revertido no saldo do empréstimo e a parcela voltará a ficar pendente. O link do recibo passará a exibir um comprovante de estorno com o motivo informado.</DialogDescription></DialogHeader>
          <div className="space-y-1"><Label className="text-xs">Motivo do estorno *</Label>
            <Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: pagamento em duplicidade, valor lançado errado..." data-testid="estorno-motivo" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEst(null)}>Cancelar</Button>
            <Button onClick={estornar} disabled={estLoading || !motivo.trim()} className="bg-red-600 hover:bg-red-700" data-testid="estornar-confirm-btn">
              {estLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Estornar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
